export const GEO_CATALOG_ROOTS = ['/veterinars/', '/clinics/', '/uslugi/'] as const

export type GeoCatalogRoot = (typeof GEO_CATALOG_ROOTS)[number]

/**
 * Город со старого городского адреса каталога, переживающий 301 на плоский узел. Кладётся
 * серверным legacy-catalog-redirect, читается один раз стором города — localStorage на этом
 * шаге ещё пуст, а редирект отдаёт сервер, и других способов донести город до клиента нет.
 */
export const CITY_FROM_REDIRECT_COOKIE = 'cityFromRedirect'

const GEO_CATALOG_CITY_FILTER_CLEARED_KEY = 'geo-catalog-city-filter-cleared'

export function isGeoCatalogSectionPath(path: string): boolean {
  return GEO_CATALOG_ROOTS.some((root) => path.startsWith(root.replace(/\/$/, '')))
}

export function isGeoCatalogOnlinePath(path: string): boolean {
  return path.includes('/online')
}

export function extractGeoCatalogCitySlug(path: string): string | null {
  if (isGeoCatalogOnlinePath(path)) {
    return null
  }

  for (const root of GEO_CATALOG_ROOTS) {
    if (!path.startsWith(root.replace(/\/$/, ''))) {
      continue
    }

    const segment = path.slice(root.length).split('/').filter(Boolean)[0]
    if (segment && !segment.startsWith('?')) {
      return segment
    }
  }

  return null
}

export function markGeoCatalogCityFilterCleared(): void {
  if (!import.meta.client) {
    return
  }

  sessionStorage.setItem(GEO_CATALOG_CITY_FILTER_CLEARED_KEY, '1')
}

export function clearGeoCatalogCityFilterCleared(): void {
  if (!import.meta.client) {
    return
  }

  sessionStorage.removeItem(GEO_CATALOG_CITY_FILTER_CLEARED_KEY)
}

export function isGeoCatalogCityFilterCleared(): boolean {
  if (!import.meta.client) {
    return false
  }

  return sessionStorage.getItem(GEO_CATALOG_CITY_FILTER_CLEARED_KEY) === '1'
}

export function normalizePathWithSlash(path: string): string {
  return path.endsWith('/') ? path : `${path}/`
}

export function isGeoCatalogRootPath(path: string): boolean {
  return GEO_CATALOG_ROOTS.includes(normalizePathWithSlash(path) as GeoCatalogRoot)
}

export function routeHasCatalogFilterQuery(query: Record<string, unknown>): boolean {
  return Object.keys(query).some((key) => key.startsWith('filter-'))
}

export function isVisitFilterQuery(query: Record<string, unknown>): boolean {
  const raw = query['filter-type']
  const value = Array.isArray(raw) ? raw[0] : raw

  return value === 'visit'
}

export function canAutoRedirectToVisitSection(
  path: string,
  query: Record<string, unknown>,
): boolean {
  if (path.includes('/online')) {
    return false
  }

  if (!isGeoCatalogRootPath(path)) {
    return false
  }

  if (!isVisitFilterQuery(query)) {
    return false
  }

  if (isGeoCatalogCityFilterCleared()) {
    return false
  }

  return true
}

export function omitFilterTypeQuery(
  query: Record<string, unknown>,
): Record<string, string> {
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(query)) {
    if (key === 'filter-type' || value === null || value === undefined) {
      continue
    }

    result[key] = Array.isArray(value) ? String(value[0]) : String(value)
  }

  return result
}

export function buildVisitSectionHref(
  citySlug: string,
  query: Record<string, unknown>,
): string {
  const path = buildVisitSectionPath(citySlug)
  const qs = new URLSearchParams(omitFilterTypeQuery(query)).toString()

  return `${path}${qs ? `?${qs}` : ''}`
}

let visitSectionRedirecting = false

export function isVisitSectionRedirecting(): boolean {
  return visitSectionRedirecting
}

export function performVisitSectionRedirect(
  citySlug: string,
  query: Record<string, unknown>,
): void {
  if (!import.meta.client || visitSectionRedirecting) {
    return
  }

  visitSectionRedirecting = true
  window.location.replace(buildVisitSectionHref(citySlug, query))
}

export function canAutoRedirectGeoCatalog(
  path: string,
  query: Record<string, unknown>,
): boolean {
  if (path.includes('/online')) {
    return false
  }

  if (!isGeoCatalogRootPath(path)) {
    return false
  }

  if (isGeoCatalogCityFilterCleared()) {
    return false
  }

  return !routeHasCatalogFilterQuery(query)
}

export function buildGeoCatalogCityPath(rootPath: string, citySlug: string): string {
  const root = normalizePathWithSlash(rootPath)

  return `${root}${citySlug}/`
}

export function buildGeoCatalogCityHref(
  rootPath: string,
  citySlug: string,
  query: Record<string, unknown>,
): string {
  const path = buildGeoCatalogCityPath(rootPath, citySlug)
  const qs = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) {
      continue
    }

    qs.set(key, Array.isArray(value) ? String(value[0]) : String(value))
  }

  const qsStr = qs.toString()

  return `${path}${qsStr ? `?${qsStr}` : ''}`
}

let geoCatalogCityRedirecting = false

export function isGeoCatalogCityRedirecting(): boolean {
  return geoCatalogCityRedirecting
}

export function performGeoCatalogCityRedirect(
  rootPath: string,
  citySlug: string,
  query: Record<string, unknown>,
): void {
  if (!import.meta.client || geoCatalogCityRedirecting) {
    return
  }

  geoCatalogCityRedirecting = true
  window.location.replace(buildGeoCatalogCityHref(rootPath, citySlug, query))
}

/** Сегмент раздела «Выезд на дом»: ЧПУ имеет вид /veterinars/{город}/vyesd/[специальность]/. */
export const VISIT_SECTION_SEGMENT = 'vyesd'

export function buildVisitSectionPath(citySlug: string, specialtySlug?: string): string {
  let path = `/veterinars/${citySlug}/${VISIT_SECTION_SEGMENT}/`

  if (specialtySlug) {
    path += `${specialtySlug}/`
  }

  return path
}

export function isVisitSectionPath(path: string): boolean {
  const segments = path.replace(/^\/+/, '').split('/').filter(Boolean)

  return segments[0] === 'veterinars' && segments[2] === VISIT_SECTION_SEGMENT
}

export function replaceVisitCitySlug(path: string, citySlug: string): string {
  const hadTrailingSlash = path.endsWith('/')
  const segments = path.replace(/^\/+/, '').replace(/\/+$/, '').split('/')

  if (segments[0] !== 'veterinars' || segments[2] !== VISIT_SECTION_SEGMENT) {
    return path
  }

  segments[1] = citySlug
  const rebuilt = `/${segments.join('/')}`

  return hadTrailingSlash ? normalizePathWithSlash(rebuilt) : rebuilt
}
